import { For, Show, createMemo, type JSX } from "solid-js";
import { errorMessage } from "../../api/errors";
import { Modal } from "../../components/Modal";
import { StatusBadge } from "../../components/StatusBadge";
import type { QuotaLimit, QuotaView, Relay } from "../../model";
import type { QuotaBoard, QuotaEntry } from "../../state/quota";
import { formatDate, formatPercent, formatQuotaDuration, formatRelativeTimestamp } from "../../utils/format";

interface QuotaPanelProps {
  relays: Relay[];
  board: QuotaBoard;
  onOpenDetails: (relay: Relay) => void;
}

function quotaTone(entry: QuotaEntry): "good" | "warning" | "danger" | "neutral" {
  if (entry.status === "error") return "danger";
  if (entry.data?.status === "fresh") return "good";
  if (entry.data?.status === "stale") return "warning";
  return "neutral";
}

function quotaLabel(entry: QuotaEntry): string {
  if (entry.status === "loading" || entry.status === "refreshing") return "查询中";
  if (entry.status === "error") return entry.data ? "旧数据" : "查询失败";
  if (!entry.data) return "尚未查询";
  if (entry.data.status === "fresh") return "Fresh";
  if (entry.data.status === "stale") return "Stale";
  return "Unavailable";
}

function flattenWindows(
  view: QuotaView | null,
  limitID?: string,
): Array<{ label: string; usedPercent: number; reset: number }> {
  if (!view) return [];
  return view.limits.filter((limit) => limitID === undefined || limit.id === limitID).flatMap((limit) =>
    limit.windows.map((window) => ({
      label: `${limit.name ?? limit.id} · ${window.kind}`,
      usedPercent: window.usedPercent,
      reset: window.resetsAt,
    })),
  );
}

function progressValue(value: number): number {
  return Math.max(0, Math.min(100, value));
}

export function QuotaSummary(props: { entry: QuotaEntry }): JSX.Element {
  const windows = createMemo(() => flattenWindows(props.entry.data, "codex"));
  return (
    <div class="quota-summary-inline">
      <StatusBadge label={quotaLabel(props.entry)} tone={quotaTone(props.entry)} />
      <Show when={windows()[0]} fallback={<Show when={props.entry.data?.status !== "unavailable" && props.entry.data !== null}><span>无 codex</span></Show>}>
        {(window) => <span>已用 {formatPercent(window().usedPercent)}</span>}
      </Show>
    </div>
  );
}

export function QuotaPanel(props: QuotaPanelProps): JSX.Element {
  return (
    <section class="bento bento--quota" aria-labelledby="quota-heading">
      <header class="bento__header">
        <div>
          <p class="eyebrow">Upstream Quota · 逐 Relay 只读观察</p>
          <h2 id="quota-heading">上游配额用量</h2>
        </div>
        <p class="bento__hint">Active Relay 自动查询，其他状态按需查询；最多并发 4 个。</p>
      </header>
      <Show
        when={props.relays.length > 0}
        fallback={<div class="empty-state"><strong>暂无 Relay</strong><span>创建 Relay 后可查看配额用量。</span></div>}
      >
        <div class="quota-grid">
          <For each={props.relays}>
            {(relay) => {
              const entry = () => props.board.entry(relay.id);
              const windows = () => flattenWindows(entry().data, "codex");
              const hasCodexLimit = () => entry().data?.limits.some((limit) => limit.id === "codex") ?? false;
              return (
                <article class="quota-card">
                  <header>
                    <div>
                      <strong>{relay.displayName}</strong>
                      <code>{relay.id}</code>
                    </div>
                    <StatusBadge label={quotaLabel(entry())} tone={quotaTone(entry())} />
                  </header>
                  <Show
                    when={windows().length > 0}
                    fallback={
                      <div class="quota-card__empty">
                        <span>
                          {entry().status === "error"
                            ? errorMessage(entry().error)
                            : entry().data?.status !== "unavailable" && entry().data !== null && !hasCodexLimit()
                              ? "上游未返回 codex Limit"
                              : entry().data?.status === "unavailable"
                                ? "上游配额不可用"
                                : relay.state === "active"
                              ? "等待配额数据"
                              : `${relay.state} Relay 不会自动查询`}
                        </span>
                      </div>
                    }
                  >
                    <div class="quota-windows">
                      <For each={windows().slice(0, 3)}>
                        {(window) => (
                          <div class="quota-window">
                            <div><span>{window.label}</span><strong>已用 {formatPercent(window.usedPercent)}</strong></div>
                            <div
                              class="quota-progress"
                              role="progressbar"
                              aria-label={`${window.label} 已用 ${formatPercent(window.usedPercent)}`}
                              aria-valuemin="0"
                              aria-valuemax="100"
                              aria-valuenow={progressValue(window.usedPercent)}
                            >
                              <span style={{ width: `${progressValue(window.usedPercent)}%` }} />
                            </div>
                            <small>重置：{formatDate(window.reset * 1000)}</small>
                          </div>
                        )}
                      </For>
                    </div>
                  </Show>
                  <footer>
                    <span>{formatRelativeTimestamp(entry().updatedAt)}</span>
                    <div>
                      <button type="button" class="text-button" onClick={() => void props.board.load(relay.id)}>
                        {entry().data ? "刷新" : "查询"}
                      </button>
                      <button type="button" class="text-button" onClick={() => props.onOpenDetails(relay)}>
                        详情
                      </button>
                    </div>
                  </footer>
                </article>
              );
            }}
          </For>
        </div>
      </Show>
    </section>
  );
}

interface QuotaDetailsModalProps {
  relay: Relay;
  entry: QuotaEntry;
  onRefresh: () => void;
  onClose: () => void;
}

function QuotaLimitView(props: { limit: QuotaLimit }): JSX.Element {
  return (
    <section class="quota-detail-limit">
      <h3>{props.limit.name ? `${props.limit.name} · ${props.limit.id}` : props.limit.id}</h3>
      <For each={props.limit.windows}>
        {(window) => (
          <div class="quota-window quota-window--detail">
            <div><span>{window.kind}</span><strong>已用 {formatPercent(window.usedPercent)}</strong></div>
            <div
              class="quota-progress"
              role="progressbar"
              aria-label={`${props.limit.name ?? props.limit.id} · ${window.kind} 已用 ${formatPercent(window.usedPercent)}`}
              aria-valuemin="0"
              aria-valuemax="100"
              aria-valuenow={progressValue(window.usedPercent)}
            ><span style={{ width: `${progressValue(window.usedPercent)}%` }} /></div>
            <small>
              窗口 {formatQuotaDuration(window.durationSeconds)} · 重置于 {formatDate(window.resetsAt * 1000)}
            </small>
          </div>
        )}
      </For>
    </section>
  );
}

export function QuotaDetailsModal(props: QuotaDetailsModalProps): JSX.Element {
  return (
    <Modal title={`${props.relay.displayName} · 上游配额`} eyebrow="Quota Snapshot" onClose={props.onClose} size="wide">
      <div class="modal-toolbar">
        <StatusBadge label={quotaLabel(props.entry)} tone={quotaTone(props.entry)} />
        <span>Plan：{props.entry.data?.planType ?? "未知"}</span>
        <span>观察时间：{formatDate(props.entry.data?.observedAt ?? null)}</span>
        <button type="button" class="button button--ghost" onClick={props.onRefresh}>刷新</button>
      </div>
      <Show when={props.entry.data?.status === "stale" || props.entry.data?.refreshFailed}>
        <p class="callout callout--warning">当前展示的是最后一次成功数据；最近刷新失败。</p>
      </Show>
      <Show when={props.entry.status === "error"}>
        <p class="callout callout--danger">{errorMessage(props.entry.error)}</p>
      </Show>
      <Show
        when={(props.entry.data?.limits.length ?? 0) > 0}
        fallback={<div class="empty-state"><strong>没有可展示的配额窗口</strong><span>Unavailable 不会被显示为 0%。</span></div>}
      >
        <div class="quota-detail-grid">
          <For each={props.entry.data?.limits ?? []}>{(limit) => <QuotaLimitView limit={limit} />}</For>
        </div>
      </Show>
    </Modal>
  );
}
